import React, { useContext } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Layout } from "./Layout";
import { AppAuthBoundary } from "./components/auth/AppAuthBoundary";
import { Auth0CallbackPage } from "./components/auth/Auth0CallbackPage";
import { Auth0LogoutPage } from "./components/auth/Auth0LogoutPage";
import { Home } from "./pages/Home";
import { Workorders } from "./pages/Workorders";
import { Workflows } from "./pages/Workflows";
import { WorkflowViewer } from "./components/workflows/WorkflowViewer";
import { Results } from "./pages/Results";
import { ResultViewer } from "./components/results/ResultViewer";
import { MeasurementPage } from "./components/results/MeasurementViewer";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faHome,
  faSitemap,
  faChartLine,
  faBolt,
  faCog,
  faBuilding,
  faUsers,
  faUserPlus,
  faUserShield,
  faFileAlt,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { AccessControlContext } from "./components/contexts/AccessControlContext";
import { AuthContext } from "./components/contexts/AuthContext";
import { WorkorderViewer } from "./components/workorders/WorkorderViewer";
import { HvSubstations } from "./pages/HvSubstations";
import { HvSubstationViewer } from "./components/hvSubstations/HvSubstationViewer";
import { Settings } from "./pages/Settings";
import { Subsets } from "./pages/Subsets";
import { SitesViewer } from "./components/settings/SitesViewer";
import { CustomerSettings } from "./pages/CustomerSettings";
import { Register } from "./pages/Register";
import { UsersManagement } from "./pages/UsersManagement";
import { Word } from "./pages/Word";
import { Reports } from "./pages/Reports";
import config from "./config";
import { ReportVersionsViewer } from "./components/reports/ReportVersionsViewer";
import Help from "./pages/Help";

export interface Props {
  children?: React.ReactNode;
  location?: unknown;
}

interface RouteComponentProps extends Props {
  isLoggedIn: boolean;
  language?: string;
}

type RouteComponent = React.ComponentType<RouteComponentProps>;

export interface Page {
  title: string;
  path: string;
  component: RouteComponent;
  routes?: Page[];
  icon: (iconProps?: Partial<FontAwesomeIconProps>) => JSX.Element;
  isMenuItem?: boolean;
  description?: string;
  breadcrumb?: string;
  isLoggedOutMenuItem?: boolean;
}

export const usePages = (): Page[] => {
  const { t } = useTranslation();
  const accessControlContext = useContext(AccessControlContext);
  const authContext = useContext(AuthContext);
  const isEaEmployee = useContext(AuthContext).amIEaEmployee();

  let pages: Page[] = [
    {
      title: t("home.title") || "",
      path: "/",
      component: Home,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faHome} {...iconProps} />,
      isMenuItem: true,
      breadcrumb: t("home.title"),
    },
    {
      title: t("substations.hvSubstations") || "",
      path: "/hvsubstations",
      component: HvSubstations,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faBolt} {...iconProps} />,
      isMenuItem: true,
      description: t("home.hvSubstationPageDescription"),
      breadcrumb: t("substations.hvSubstationList"),
    },
    {
      title: t("substations.hvSubstation") || "",
      path: "/hvsubstations/hvsubstationview/:assetId",
      component: HvSubstationViewer,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faBolt} {...iconProps} />,
      breadcrumb: "substations.hvSubstation",
    },
    {
      title: t("register.title") || "",
      path: "/register",
      component: Register,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faUserPlus} {...iconProps} />,
      isMenuItem: false,
      breadcrumb: t("register.title"),
      isLoggedOutMenuItem: true,
    },
    {
      title: "Word",
      path: "/word/:fileId/:customerId/:companyId/:siteId",
      component: Word,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faUserPlus} {...iconProps} />,
      isMenuItem: false,
      breadcrumb: "Word Convert",
      isLoggedOutMenuItem: false,
    },
    {
      title: t("reports.reportVersions") || "",
      path: "/reports/reportversionsview/:reportDocumentId/:customerId/:companyId/:siteId",
      component: ReportVersionsViewer,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faFileAlt} {...iconProps} />,
      breadcrumb: t("reports.reportVersions"),
    },
    {
      title: "Help",
      path: "/help",
      component: Help,
      icon: (iconProps) => <FontAwesomeIcon icon={faQuestionCircle} {...iconProps} />,
      isMenuItem: true,
      breadcrumb: "Help",
      isLoggedOutMenuItem: true,
    },
  ];

  accessControlContext.canI("view", "workflow") &&
    pages.push({
      title: t("workflows.workflows") || "",
      path: "/workflows",
      component: Workflows,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faSitemap} {...iconProps} />,
      isMenuItem: true,
      description: t("home.workflowPageDescription"),
      breadcrumb: t("workflows.workflowList"),
    }) &&
    pages.push({
      title: t("workflows.workflow") || "",
      path: "/workflows/workflowview/:workflowId",
      component: WorkflowViewer,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faSitemap} {...iconProps} />,
      breadcrumb: t("workflows.workflow"),
    });

  accessControlContext.canI("view", "workorder") &&
    pages.push({
      title: t("workorders.workorders") || "",
      path: "/workorders",
      component: Workorders,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faClipboardList} {...iconProps} />,
      isMenuItem: true,
      description: t("home.workOrderPageDescription"),
      breadcrumb: t("workorders.workorderList"),
    }) &&
    pages.push({
      title: t("workorders.workorder") || "",
      path: "/workorders/workorderview/:workorderId/:customerId/:companyId/:siteId",
      component: WorkorderViewer,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faClipboardList} {...iconProps} />,
      breadcrumb: t("workorders.workorder"),
    });

  authContext.canI("view", "results") &&
    pages.push({
      title: t("results.results") || "",
      path: "/results",
      component: Results,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faChartLine} {...iconProps} />,
      isMenuItem: true,
      description: t("home.resultPageDescription"),
      breadcrumb: t("results.resultsList"),
    }) &&
    pages.push({
      title: t("results.result") || "",
      path: "/results/resultview/:resultType/:captureId/:switchgearAssetId/:customerId/:companyId/:siteId",
      component: ResultViewer,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faChartLine} {...iconProps} />,
      breadcrumb: t("results.result"),
    }) &&
    pages.push({
      title: t("results.measurementType") || "",
      path: "/results/resultview/:resultType/:captureId/:switchgearAssetId/measurement/:componentAssetId/:measurementType/:customerId/:companyId/:siteId",
      component: MeasurementPage,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faChartLine} {...iconProps} />,
      breadcrumb: t("results.measurementType"),
    });

  config.reportsEnabled &&
    isEaEmployee &&
    authContext.canI("view", "reportsList") &&
    pages.push({
      title: t("reports.title") || "",
      path: "/reports",
      component: Reports,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faFileAlt} {...iconProps} />,
      isMenuItem: true,
      description: t("home.reportsPageDescription"),
      breadcrumb: t("reports.reportsList"),
    });

  authContext.canI("view", "companies") &&
    pages.push({
      title: t("settings.settings") || "",
      path: "/settings",
      component: Settings,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faCog} {...iconProps} />,
      isMenuItem: true,
      description: t("home.settingsPageDescription"),
      breadcrumb: t("settings.settings"),
    }) &&
    pages.push({
      title: t("settings.subsets") || "",
      path: "/settings/subsets",
      component: Subsets,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faBuilding} {...iconProps} />,
      breadcrumb: t("settings.subsets"),
    }) &&
    pages.push({
      title: t("settings.subsets") || "",
      path: "/settings/sitesview/:companyName",
      component: SitesViewer,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faBuilding} {...iconProps} />,
      breadcrumb: t("settings.subsets"),
    });

  authContext.canI("view", "customerSettings") &&
    pages.push({
      title: t("settings.customerSettings") || "",
      path: "/settings/customerSettings",
      component: CustomerSettings,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faUsers} {...iconProps} />,
      breadcrumb: t("settings.customerSettings"),
    });

  authContext.canI("view", "users") &&
    pages.push({
      title: t("settings.usersManagement") || "",
      path: "/settings/usersManagement",
      component: UsersManagement,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faUsers} {...iconProps} />,
      breadcrumb: t("settings.usersManagement"),
    });

  const helpPage = pages.find((page) => page.path === "/help");
  pages = pages.filter((page) => page.path !== "/help");

  if (helpPage) {
    pages.push(helpPage);
  }

  return pages;
};

function getRouteComponentName(component: RouteComponent): string | undefined {
  return typeof component === "function" ? component.name : undefined;
}

export const useSettingsPages = (): Page[] => {
  const { t } = useTranslation();

  let settingsPages: Page[] = [
    {
      title: t("settings.subsets") || "",
      path: "/settings/subsets",
      component: Subsets,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faBuilding} {...iconProps} />,
      isMenuItem: true,
      description: t("settings.subsetsPageDescription"),
      breadcrumb: t("settings.subsets"),
    },
    {
      title: t("settings.customerSettings") || "",
      path: "/settings/customerSettings",
      component: CustomerSettings,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faUserShield} {...iconProps} />,
      isMenuItem: true,
      description: t("settings.customerSettingsPageDescription"),
      breadcrumb: t("settings.customerSettings"),
    },
    {
      title: t("settings.usersManagement") || "",
      path: "/settings/usersManagement",
      component: UsersManagement,
      icon: (iconProps?: Partial<FontAwesomeIconProps>) => <FontAwesomeIcon icon={faUsers} {...iconProps} />,
      isMenuItem: true,
      description: t("settings.usersManagementPageDescription"),
      breadcrumb: t("settings.usersManagement"),
    },
  ];

  return settingsPages;
};

export function PageIcon(props: { pagePath: string | undefined; iconProps?: Partial<FontAwesomeIconProps> }) {
  const pages = usePages();
  const page = pages.find((page) => page.path === props.pagePath);
  return page?.icon(props.iconProps) || null;
}

export const usePageIcon = (componentName: string) => {
  const pages = usePages();
  const page = pages.find((page) => getRouteComponentName(page.component) === componentName);
  return page?.icon();
};

export const usePage = (componentName: string) => {
  const pages = usePages();
  const page = pages.find((page) => getRouteComponentName(page.component) === componentName);

  return page;
};

export function DocumentTitle(props: { title: string }) {
  const { t } = useTranslation();
  document.title = `${props.title} | ${t("projectTitle")}`;
  return null;
}

interface PrivateRouteProps extends Page, RouteComponentProps {
  exact?: boolean;
}

function PrivateRoute({ component: Component, title, ...rest }: PrivateRouteProps) {
  return (
    <>
      <DocumentTitle title={title} />
      <Component {...rest} />
    </>
  );
}

export function AppRoutes() {
  const pages = usePages();
  const authContext = useContext(AuthContext);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth0/callback" element={<Auth0CallbackPage />} />
        <Route path="/auth0/logout" element={<Auth0LogoutPage />} />
        <Route element={<AppAuthBoundary />}>
          <Route element={<Layout />}>
            {pages.map((page) => (
              <Route
                path={page.path}
                key={page.path}
                element={
                  <PrivateRoute
                    exact
                    {...page}
                    component={page.component}
                    isLoggedIn={authContext.userIsAuthenticated}
                    language={navigator.language}
                  />
                }
              />
            ))}
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
